import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, KeyRound, FolderLock, UserPlus } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import PasswordInput from "../components/PasswordInput";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";
import { rawSignUp, rawSignIn } from "../lib/gotrueAuth";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useCustomerAuth() || {};
  const nav = useNavigate();

  const [state, setState] = useState({ status: "polling", attempts: 0, data: null });
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", attempts: 0, data: null });
      return;
    }
    const poll = async (attempts) => {
      if (stopped.current) return;
      if (attempts >= 10) {
        setState({ status: "timeout", attempts, data: null });
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", attempts, data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", attempts, data });
          return;
        }
        setState({ status: "polling", attempts, data });
        setTimeout(() => poll(attempts + 1), 2000);
      } catch (e) {
        setState({ status: "error", attempts, data: null });
      }
    };
    poll(0);
    return () => { stopped.current = true; };
  }, [sessionId]);

  // Lock the navigation whenever a guest user is on this success page
  // (polling or paid) — prevents bypass while signup is pending.
  // Logged-in users see normal nav.
  const lockNav =
    !user && (state.status === "polling" || state.status === "paid");

  return (
    <Layout locked={lockNav}>
      <section className="min-h-[70vh] flex items-center py-16 md:py-24">
        <div className="max-w-xl mx-auto px-5 sm:px-6 md:px-12 text-center w-full">
          {state.status === "polling" && <Polling attempts={state.attempts} />}
          {state.status === "paid" && (
            user ? (
              <PaidLoggedIn data={state.data} sessionId={sessionId} onGoToCases={() => nav("/meus-casos")} />
            ) : (
              <PaidGuest data={state.data} sessionId={sessionId} onSignedIn={() => nav("/meus-casos")} />
            )
          )}
          {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
            <Failed reason={state.status} />
          )}
        </div>
      </section>
    </Layout>
  );
}

// -----------------------------------------------------------------
function Polling({ attempts }) {
  return (
    <div className="dossier p-8 sm:p-12">
      <Loader2 className="w-12 h-12 text-blood mx-auto mb-6 animate-spin" />
      <h1 className="headline text-2xl sm:text-3xl md:text-4xl mb-3">A confirmar pagamento</h1>
      <p className="text-bone-muted mono text-xs sm:text-sm tracking-[0.2em]">
        // VERIFICAÇÃO {String(attempts + 1).padStart(2, "0")}/10
      </p>
    </div>
  );
}

function PaidLoggedIn({ data, sessionId, onGoToCases }) {
  return (
    <div className="dossier p-8 sm:p-12" data-testid="paid-loggedin">
      <CheckCircle2 className="w-14 h-14 text-blood mx-auto mb-6" />
      <p className="mono text-[11px] tracking-[0.35em] text-blood mb-4">PAGAMENTO CONFIRMADO</p>
      <h1 className="headline text-3xl sm:text-4xl md:text-5xl mb-4">Caso atribuído.</h1>
      <p className="text-bone-dim leading-relaxed mb-2">
        O dossier está desbloqueado na tua conta.
      </p>
      <p className="mono text-[10px] text-bone-faded tracking-[0.2em] mb-8">
        SESSÃO: {sessionId?.slice(0, 22)}...
      </p>
      <button
        onClick={onGoToCases}
        data-testid="paid-goto-cases-btn"
        className="btn-primary mx-auto"
      >
        Os meus casos <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// Guest user: show inline signup with email pre-filled
function PaidGuest({ data, sessionId, onSignedIn }) {
  const emailFromStripe = data?.customer_email || "";
  const [email, setEmail] = useState(emailFromStripe);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [pending, setPending] = useState(false); // requires email confirmation
  const [accountExists, setAccountExists] = useState(false);
  const inflight = useRef(false);

  useEffect(() => {
    if (emailFromStripe && !email) setEmail(emailFromStripe);
  }, [emailFromStripe]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (inflight.current) return;
    setError("");
    setInfo("");
    if (!isSupabaseConfigured) {
      setError("Autenticação não configurada.");
      return;
    }
    if (!email) {
      setError("Indica o teu email.");
      return;
    }
    if (password.length < 8) {
      setError("A password deve ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("As passwords não coincidem.");
      return;
    }
    inflight.current = true;
    setBusy(true);
    try {
      // 1. Sign up via raw GoTrue API (bypasses supabase-js body-stream bug)
      const signupResult = await rawSignUp(email.toLowerCase(), password);

      if (signupResult.ok) {
        // Either we got a session (auto-confirm) or need email confirmation
        if (signupResult.session) {
          onSignedIn();
          return;
        }
        if (signupResult.needsConfirmation) {
          setPending(true);
          return;
        }
      }

      // Account exists — try to sign in with the typed password
      if (signupResult.exists) {
        const signinResult = await rawSignIn(email.toLowerCase(), password);
        if (signinResult.ok && signinResult.session) {
          onSignedIn();
          return;
        }
        // Wrong password OR email confirmation pending — show friendly view
        setAccountExists(true);
        return;
      }

      // Other signup error
      setError(signupResult.error || "Não foi possível criar a conta.");
    } catch (err) {
      setError(err?.message || "Não foi possível criar a conta.");
    } finally {
      setBusy(false);
      inflight.current = false;
    }
  };

  if (accountExists) {
    return (
      <div className="dossier p-8 sm:p-10 text-center" data-testid="account-exists">
        <CheckCircle2 className="w-12 h-12 text-blood mx-auto mb-5" />
        <p className="mono text-[11px] tracking-[0.3em] text-blood mb-3">PAGAMENTO REGISTADO</p>
        <h2 className="headline text-2xl sm:text-3xl mb-4">Já tens conta connosco</h2>
        <p className="text-bone-dim text-sm sm:text-base leading-relaxed mb-3">
          Já existe uma conta com este email. Faz login para aceder ao teu caso.
        </p>
        <p className="mono text-[11px] text-bone-muted tracking-[0.2em] mb-8 break-all">{email}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={`/entrar?redirect=/meus-casos`} data-testid="exists-go-login">
            <button className="btn-primary w-full sm:w-auto justify-center">
              Entrar com este email <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <Link to="/recuperar-password" data-testid="exists-forgot">
            <button className="btn-ghost w-full sm:w-auto justify-center">Esqueceste a password?</button>
          </Link>
        </div>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="dossier p-8 sm:p-10" data-testid="signup-pending">
        <CheckCircle2 className="w-14 h-14 text-blood mx-auto mb-5" />
        <p className="mono text-[11px] tracking-[0.3em] text-blood mb-3">CONTA CRIADA</p>
        <h2 className="headline text-2xl sm:text-3xl mb-3">Confirma o teu email</h2>
        <p className="text-bone-muted text-sm sm:text-base leading-relaxed mb-2">
          Enviámos um link de ativação para
        </p>
        <p className="text-white text-sm mb-6 break-all">{email}</p>
        <p className="mono text-[10px] text-bone-faded tracking-[0.2em] leading-relaxed mb-8">
          Depois de confirmares, faz login e o caso estará lá à tua espera.
        </p>
        <Link to="/entrar">
          <button className="btn-ghost mx-auto">Ir para o login</button>
        </Link>
      </div>
    );
  }

  return (
    <div className="dossier p-7 sm:p-10 text-left" data-testid="paid-guest-signup">
      <div className="text-center mb-7">
        <CheckCircle2 className="w-12 h-12 text-blood mx-auto mb-4" />
        <p className="mono text-[10px] tracking-[0.35em] text-blood mb-3">PAGAMENTO CONFIRMADO</p>
        <h1 className="headline text-2xl sm:text-3xl md:text-4xl mb-3">Cria a tua conta</h1>
        <p className="text-bone-muted text-sm leading-relaxed">
          Define uma password para acederes sempre ao teu dossier em <span className="text-blood">"Os meus casos"</span>.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">EMAIL</label>
          {emailFromStripe ? (
            <div className="bg-ink-200 border border-white/10 px-4 py-3 mono text-sm text-white flex items-center gap-2">
              <UserPlus className="w-3.5 h-3.5 text-blood shrink-0" />
              <span className="break-all">{email}</span>
            </div>
          ) : (
            <input
              data-testid="guest-signup-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="cec-input"
              autoComplete="email"
              autoFocus
            />
          )}
        </div>

        <div>
          <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">PASSWORD</label>
          <PasswordInput
            testId="guest-signup-password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            minLength={8}
            autoFocus={Boolean(emailFromStripe)}
          />
        </div>

        <div>
          <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">CONFIRMAR PASSWORD</label>
          <PasswordInput
            testId="guest-signup-confirm"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        {error && (
          <p className="mono text-xs text-blood tracking-[0.15em] leading-relaxed" data-testid="signup-error">
            {String(error)}
          </p>
        )}
        {info && (
          <p className="mono text-xs text-bone-muted tracking-[0.15em] leading-relaxed">{info}</p>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="guest-signup-submit"
          className="btn-primary w-full justify-center disabled:opacity-50"
        >
          {busy ? "A criar conta..." : (<>Criar conta e desbloquear caso <KeyRound className="w-4 h-4" /></>)}
        </button>
      </form>

      <div className="mt-7 pt-5 border-t border-white/10 text-center">
        <p className="mono text-[10px] text-bone-faded tracking-[0.2em] leading-relaxed mb-2">
          Já tens conta com este email?
        </p>
        <Link to="/entrar" data-testid="guest-go-login" className="mono text-[11px] text-blood hover:underline tracking-[0.2em]">
          Entrar em vez disso →
        </Link>
      </div>
    </div>
  );
}

function Failed({ reason }) {
  return (
    <div className="dossier p-8 sm:p-12">
      <XCircle className="w-14 h-14 text-blood mx-auto mb-6" />
      <h1 className="headline text-2xl sm:text-3xl mb-4">
        {reason === "expired" ? "Sessão expirada" : "Não foi possível confirmar"}
      </h1>
      <p className="text-bone-muted mb-8 leading-relaxed">
        {reason === "expired"
          ? "A sessão de pagamento expirou. Tenta de novo."
          : "Verifica o teu email — se foste cobrado, contacta-nos."}
      </p>
      <Link to="/loja">
        <button className="btn-ghost mx-auto">Voltar à loja</button>
      </Link>
    </div>
  );
}
